import { pdf } from '@react-pdf/renderer';
import { EmergencyPlanDocument, EmergencyPlanData } from './template';
import { planQueries, storageHelpers } from '@/db/queries';
import { supabase } from '@/integrations/supabase/client';

/**
 * PDF Generator Agent
 *
 * Generates professional emergency plan PDFs from interview and research data
 * - Compiles data from multiple research agents
 * - Renders React-PDF template to blob
 * - Uploads to Supabase storage
 * - Updates emergency_plans table
 */

export interface GeneratePDFInput {
  interviewId: string;
  userId: string;
  interviewData: any; // Interview table row
  researchData: {
    location_risks?: any;
    emergency_services?: any;
    resources?: any;
    hazard_analysis?: any;
  };
}

export interface GeneratePDFOutput {
  planId: string;
  pdfUrl: string;
  fileSize: number;
  pageCount: number;
  generatedAt: string;
}

export class PDFGeneratorAgent {
  /**
   * Generate comprehensive emergency plan PDF
   */
  async generate(input: GeneratePDFInput): Promise<GeneratePDFOutput> {
    const startTime = Date.now();

    try {
      console.log('[PDFGeneratorAgent] Starting PDF generation for interview:', input.interviewId);

      // Step 1: Compile plan data from interview + research
      const planData = this.compilePlanData(input);

      // Step 2: Create emergency_plans record
      const plan = await planQueries.create({
        interview_id: input.interviewId,
        user_id: input.userId,
        plan_data: planData as any,
        is_latest: true,
      });

      console.log('[PDFGeneratorAgent] Created plan record:', plan.id);

      // Step 3: Render React-PDF to blob
      const pdfBlob = await this.renderPDF(planData);
      const fileSize = pdfBlob.size;

      console.log('[PDFGeneratorAgent] Rendered PDF blob:', { fileSize });

      // Step 4: Upload to Supabase storage
      const pdfUrl = await storageHelpers.uploadPlanPdf(
        input.userId,
        plan.id,
        pdfBlob
      );

      console.log('[PDFGeneratorAgent] Uploaded PDF to storage:', pdfUrl);

      // Step 5: Update plan record with PDF URL and metadata
      const pageCount = this.estimatePageCount(planData);
      await planQueries.updatePdfUrl(plan.id, pdfUrl, {
        file_size: fileSize,
        page_count: pageCount,
      });

      // Step 6: Mark old plans as not latest
      await this.markOldPlansAsNotLatest(input.interviewId, plan.id);

      const executionTime = Date.now() - startTime;
      console.log('[PDFGeneratorAgent] PDF generation complete:', {
        planId: plan.id,
        executionTime,
        fileSize,
        pageCount,
      });

      // Store completion status in memory
      await this.notifyCompletion(plan.id, pdfUrl);

      return {
        planId: plan.id,
        pdfUrl,
        fileSize,
        pageCount,
        generatedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error('[PDFGeneratorAgent] PDF generation failed:', error);
      throw new Error(
        `PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Compile all data into EmergencyPlanData format
   */
  private compilePlanData(input: GeneratePDFInput): EmergencyPlanData {
    const { interviewData, researchData } = input;

    // Extract JSONB fields from interview data
    const location = typeof interviewData.location === 'string'
      ? JSON.parse(interviewData.location)
      : interviewData.location;

    const household = typeof interviewData.household === 'string'
      ? JSON.parse(interviewData.household)
      : interviewData.household || {};

    const property = typeof interviewData.property === 'string'
      ? JSON.parse(interviewData.property)
      : interviewData.property || {};

    const medicalNeeds = typeof interviewData.medical_needs === 'string'
      ? JSON.parse(interviewData.medical_needs)
      : interviewData.medical_needs || {};

    return {
      interview: {
        name: interviewData.name,
        email: interviewData.email,
        phone: interviewData.phone,
        location: {
          address: location.address || '',
          city: location.city,
          state: location.state,
          zip_code: location.zip,
          emergency_contact: location.emergency_contact,
        },
        household: household,
        property: property,
        emergency_types: interviewData.emergency_types,
        medical_needs: medicalNeeds,
        budget_range: interviewData.budget_range,
      },
      location_risks: researchData.location_risks,
      emergency_services: researchData.emergency_services,
      resources: researchData.resources,
      hazard_analysis: researchData.hazard_analysis,
      generated_at: new Date().toISOString(),
      version: '1.0',
    };
  }

  /**
   * Render React-PDF template to Blob
   */
  private async renderPDF(data: EmergencyPlanData): Promise<Blob> {
    try {
      // Create React element
      const doc = EmergencyPlanDocument({ data });

      // Render to blob using @react-pdf/renderer
      const blob = await pdf(doc).toBlob();

      return blob;
    } catch (error) {
      console.error('[PDFGeneratorAgent] PDF rendering error:', error);
      throw new Error(`Failed to render PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Estimate page count based on data
   */
  private estimatePageCount(data: EmergencyPlanData): number {
    // Base pages: Cover, ToC, Summary, Risk, Services, Supplies, Contacts
    let pages = 7;

    // Additional pages for emergency procedures (1 page per emergency type)
    pages += data.interview.emergency_types.length;

    // Additional pages for detailed research data
    if (data.emergency_services?.hospitals?.length > 3) pages += 1;
    if (data.emergency_services?.shelters?.length > 3) pages += 1;
    if (data.resources) pages += 2;
    if (data.hazard_analysis) pages += 1;

    return pages;
  }

  /**
   * Mark old plans as not latest for this interview
   */
  private async markOldPlansAsNotLatest(
    interviewId: string,
    currentPlanId: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('emergency_plans')
        .update({ is_latest: false })
        .eq('interview_id', interviewId)
        .neq('id', currentPlanId);

      if (error) {
        console.error('[PDFGeneratorAgent] Error marking old plans:', error);
        // Don't throw - this is not critical
      }
    } catch (error) {
      console.error('[PDFGeneratorAgent] Failed to mark old plans:', error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Notify completion via hooks
   */
  private async notifyCompletion(planId: string, pdfUrl: string): Promise<void> {
    try {
      const { exec } = await import('child_process');
      const { promisify } = await import('util');
      const execAsync = promisify(exec);

      await execAsync(
        `npx claude-flow@alpha hooks notify --message "PDF generated: ${planId} at ${pdfUrl}"`
      );
    } catch (error) {
      // Hook notification is optional - don't fail if it errors
      console.warn('[PDFGeneratorAgent] Hook notification failed:', error);
    }
  }

  /**
   * Get PDF download URL for existing plan
   */
  async getDownloadUrl(planId: string): Promise<string | null> {
    try {
      const plan = await supabase
        .from('emergency_plans')
        .select('pdf_url')
        .eq('id', planId)
        .single();

      if (plan.error) throw plan.error;

      return plan.data?.pdf_url || null;
    } catch (error) {
      console.error('[PDFGeneratorAgent] Failed to get download URL:', error);
      return null;
    }
  }

  /**
   * Regenerate PDF for existing plan
   */
  async regenerate(planId: string): Promise<GeneratePDFOutput> {
    try {
      // Get existing plan
      const { data: plan, error: planError } = await supabase
        .from('emergency_plans')
        .select('*, interview_id, user_id')
        .eq('id', planId)
        .single();

      if (planError || !plan) {
        throw new Error('Plan not found');
      }

      // Get interview data
      const { data: interview, error: interviewError } = await supabase
        .from('interviews')
        .select('*')
        .eq('id', plan.interview_id)
        .single();

      if (interviewError || !interview) {
        throw new Error('Interview not found');
      }

      // Get research data
      const { data: researchRecords, error: researchError } = await supabase
        .from('research_data')
        .select('*')
        .eq('interview_id', plan.interview_id);

      if (researchError) {
        throw new Error('Failed to fetch research data');
      }

      // Compile research data
      const researchData: any = {};
      researchRecords?.forEach((record) => {
        researchData[record.agent_type] = record.data;
      });

      // Generate new PDF
      return await this.generate({
        interviewId: plan.interview_id,
        userId: plan.user_id,
        interviewData: interview,
        researchData,
      });
    } catch (error) {
      console.error('[PDFGeneratorAgent] Regeneration failed:', error);
      throw new Error(
        `PDF regeneration failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get generation status and metadata
   */
  async getStatus(planId: string): Promise<{
    exists: boolean;
    pdfUrl: string | null;
    metadata: any;
    generatedAt: string | null;
  }> {
    try {
      const { data: plan, error } = await supabase
        .from('emergency_plans')
        .select('pdf_url, pdf_metadata, updated_at')
        .eq('id', planId)
        .single();

      if (error) {
        return {
          exists: false,
          pdfUrl: null,
          metadata: null,
          generatedAt: null,
        };
      }

      return {
        exists: true,
        pdfUrl: plan.pdf_url,
        metadata: plan.pdf_metadata,
        generatedAt: plan.updated_at,
      };
    } catch (error) {
      console.error('[PDFGeneratorAgent] Status check failed:', error);
      return {
        exists: false,
        pdfUrl: null,
        metadata: null,
        generatedAt: null,
      };
    }
  }
}

/**
 * Create PDF generator agent instance
 */
export function createPDFGeneratorAgent(): PDFGeneratorAgent {
  return new PDFGeneratorAgent();
}

/**
 * Quick helper: Generate PDF from interview ID
 */
export async function generatePDFFromInterview(
  interviewId: string,
  userId: string
): Promise<GeneratePDFOutput> {
  const agent = createPDFGeneratorAgent();

  // Fetch interview data
  const { data: interview, error: interviewError } = await supabase
    .from('interviews')
    .select('*')
    .eq('id', interviewId)
    .single();

  if (interviewError || !interview) {
    throw new Error('Interview not found');
  }

  // Fetch all research data
  const { data: researchRecords, error: researchError } = await supabase
    .from('research_data')
    .select('*')
    .eq('interview_id', interviewId);

  if (researchError) {
    throw new Error('Failed to fetch research data');
  }

  // Compile research data by agent type
  const researchData: any = {};
  researchRecords?.forEach((record) => {
    researchData[record.agent_type] = record.data;
  });

  // Generate PDF
  return await agent.generate({
    interviewId,
    userId,
    interviewData: interview,
    researchData,
  });
}
